import { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { getTeamsAuthContext, parseJwtToken } from './teamsAuth';

export interface UnifiedAuthState {
  isAuthenticated: boolean;
  inProgress: string;
  account: {
    localAccountId: string;
    name?: string;
    username: string;
    idTokenClaims?: any;
  } | null;
  isInTeams: boolean;
}

/**
 * Custom hook that provides unified authentication state for both Teams and MSAL
 * Use this instead of useMsal() and useIsAuthenticated() for Teams compatibility
 */
export function useUnifiedAuth(): UnifiedAuthState {
  const teamsContext = getTeamsAuthContext();
  const msalAuthState = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();
  
  // Initialize state based on Teams context
  const getInitialState = (): UnifiedAuthState => {
    if (teamsContext.isInTeams) {
      if (teamsContext.token && teamsContext.userInfo) {
        const tokenClaims = parseJwtToken(teamsContext.token);
        return {
          isAuthenticated: true,
          inProgress: 'none',
          account: {
            localAccountId: teamsContext.userInfo.id,
            name: teamsContext.userInfo.name,
            username: teamsContext.userInfo.email || teamsContext.userInfo.name,
            idTokenClaims: tokenClaims
          },
          isInTeams: true
        };
      } else {
        return {
          isAuthenticated: false,
          inProgress: 'none',
          account: null,
          isInTeams: true
        };
      }
    }
    
    // Not in Teams - use startup state and wait for MSAL
    return {
      isAuthenticated: false,
      inProgress: 'startup',
      account: null,
      isInTeams: false
    };
  };
  
  const [authState, setAuthState] = useState<UnifiedAuthState>(getInitialState());

  useEffect(() => {
    if (teamsContext.isInTeams) {
      // Teams authentication
      if (teamsContext.token && teamsContext.userInfo) {
        const tokenClaims = parseJwtToken(teamsContext.token);
        
        setAuthState({
          isAuthenticated: true,
          inProgress: 'none',
          account: {
            localAccountId: teamsContext.userInfo.id,
            name: teamsContext.userInfo.name,
            username: teamsContext.userInfo.email || teamsContext.userInfo.name,
            idTokenClaims: tokenClaims
          },
          isInTeams: true
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          inProgress: 'none',
          account: null,
          isInTeams: true
        });
      }
    } else {
      // MSAL authentication
      const msalAccount = accounts[0];
      setAuthState({
        isAuthenticated: msalAuthState,
        inProgress,
        account: msalAccount ? {
          localAccountId: msalAccount.localAccountId,
          name: msalAccount.name,
          username: msalAccount.username,
          idTokenClaims: msalAccount.idTokenClaims
        } : null,
        isInTeams: false
      });
    }
  }, [teamsContext.isInTeams, teamsContext.token, teamsContext.userInfo, msalAuthState, accounts, inProgress]);

  return authState;
}
